// Generated by GitHub Copilot
import profileFallback from "../data/profileFallback";

export const VISITOR_MODES = Object.freeze({
  HIRING: "hiring",
  BROWSING: "browsing",
});

export const VISITOR_MODE_STORAGE_KEY = "dragon-portfolio-visitor-mode";

const defaultPromptBase =
  "You are a concise assistant for Ram Prakash Dhulipudi's portfolio. Prioritize factual project summaries and practical impact.";

const hiringPromptSuffix =
  "Visitor intent is hiring. Answer like a recruiter-ready briefing: concise, evidence-backed, and role-fit focused. Use only verifiable portfolio facts and include direct contact options when relevant.";

const browsingPromptSuffix =
  "Visitor intent is browsing. Respond with strict portfolio facts only. Avoid speculation, assumptions, or generic career coaching.";

/**
 * Normalize text by trimming and reducing whitespace.
 *
 * @param {unknown} value Raw text input.
 * @param {number} maxLength Maximum allowed length.
 * @returns {string} Sanitized string.
 */
function sanitizeText(value, maxLength) {
  return String(value || "").replace(/\s+/g, " ").trim().slice(0, maxLength);
}

/**
 * Normalize any user mode input into a supported visitor mode.
 *
 * @param {unknown} value Potential visitor mode.
 * @returns {"hiring" | "browsing" | ""} Normalized visitor mode or empty string.
 */
export function normalizeVisitorMode(value) {
  const normalized = sanitizeText(value, 32).toLowerCase();
  if (normalized === VISITOR_MODES.HIRING || normalized === VISITOR_MODES.BROWSING) {
    return normalized;
  }

  return "";
}

/**
 * Compose a mode-aware system prompt while preserving a safe prompt size.
 *
 * @param {string | undefined} basePrompt Base prompt from configuration.
 * @param {unknown} visitorMode Selected visitor mode.
 * @returns {string} Final system prompt for backend chat.
 */
export function buildModeSystemPrompt(basePrompt, visitorMode) {
  const safeBasePrompt = sanitizeText(basePrompt || defaultPromptBase, 500);
  const normalizedMode = normalizeVisitorMode(visitorMode) || VISITOR_MODES.BROWSING;

  if (normalizedMode === VISITOR_MODES.HIRING) {
    return `${safeBasePrompt} ${hiringPromptSuffix}`;
  }

  return `${safeBasePrompt} ${browsingPromptSuffix}`;
}

/**
 * Resolve the first assistant message based on visitor mode state.
 *
 * @param {string | undefined} configuredMessage Message configured in site settings.
 * @param {unknown} visitorMode Visitor mode selected in the current session.
 * @returns {string} Intro message displayed to the user.
 */
export function getInitialAssistantMessage(configuredMessage, visitorMode) {
  const normalizedMode = normalizeVisitorMode(visitorMode);
  if (normalizedMode === VISITOR_MODES.HIRING) {
    return "Hiring mode is active. Ask about role fit, impact, certifications, or direct contact details.";
  }

  if (normalizedMode === VISITOR_MODES.BROWSING) {
    return "Browsing mode is active. Ask about projects, skills, certifications, and portfolio facts.";
  }

  return (
    configuredMessage ||
    "Are you here for hiring or just browsing? Select a mode first so I can tailor accurate responses."
  );
}

/**
 * Check whether a query includes one of the provided terms.
 *
 * @param {string} query Normalized user query.
 * @param {string[]} terms Terms to look for.
 * @returns {boolean} True when any term is present.
 */
function hasAnyTerm(query, terms) {
  return terms.some((term) => query.includes(term));
}

/**
 * Gather concise experience highlights that match query terms.
 *
 * @param {Array<{company?: string, role?: string, highlights?: string[]}>} entries Experience entries.
 * @param {string[]} terms Query terms.
 * @param {number} maxItems Maximum highlights to return.
 * @returns {string[]} Matched highlights.
 */
function collectExperienceHighlights(entries, terms, maxItems = 2) {
  const safeEntries = Array.isArray(entries) ? entries : [];
  const safeTerms = Array.isArray(terms) ? terms.filter(Boolean) : [];
  const results = [];

  for (const entry of safeEntries) {
    if (!entry || results.length >= maxItems) {
      continue;
    }

    const company = sanitizeText(entry.company || "", 120);
    const role = sanitizeText(entry.role || "", 120);
    const highlights = Array.isArray(entry.highlights) ? entry.highlights : [];

    for (const rawHighlight of highlights) {
      if (results.length >= maxItems) {
        break;
      }

      const highlight = sanitizeText(rawHighlight || "", 220);
      if (!highlight) {
        continue;
      }

      const searchableText = `${company} ${role} ${highlight}`.toLowerCase();
      if (safeTerms.some((term) => searchableText.includes(term))) {
        results.push(`At ${company || "recent roles"}: ${highlight}`);
      }
    }
  }

  return results;
}

/**
 * Build a concise hiring snapshot using only known profile facts.
 *
 * @param {Record<string, any>} profile Portfolio profile payload.
 * @returns {string} Structured hiring snapshot.
 */
function buildHiringSnapshot(profile) {
  const identity = profile?.identity || {};
  const experience = Array.isArray(profile?.experience) ? profile.experience : [];
  const topSkills = Array.isArray(profile?.topSkills) ? profile.topSkills : [];
  const certifications = Array.isArray(profile?.certifications) ? profile.certifications : [];
  const contact = profile?.contact || {};

  const name = sanitizeText(identity.name || "Ram Prakash Dhulipudi", 120);
  const headline = sanitizeText(identity.headline || "ML Engineer", 220);
  const currentRole = experience[0]
    ? `${sanitizeText(experience[0].role || "", 90)} at ${sanitizeText(experience[0].company || "", 90)}`
    : "ML Engineer and Packaged App Developer";
  const skills = topSkills
    .slice(0, 3)
    .map((item) => sanitizeText(item?.name || "", 60))
    .filter(Boolean)
    .join(", ");
  const topCertification = sanitizeText(certifications[0] || "", 140);
  const email = sanitizeText(contact.email || "", 150);
  const linkedin = sanitizeText(contact.linkedin || "", 180);

  return `Hiring snapshot: ${name} | ${headline}. Current role: ${currentRole}. Core strengths: ${skills || "Python, ML, React"}.${topCertification ? ` Certification: ${topCertification}.` : ""} Contact: ${email}${linkedin ? ` | ${linkedin}` : ""}.`;
}

/**
 * Build a deterministic local portfolio reply for frontend fallback mode.
 *
 * @param {string} message User message text.
 * @param {unknown} visitorMode Selected visitor mode.
 * @param {Record<string, any>} [profile] Optional profile payload override.
 * @returns {string} Chat reply built from local profile facts.
 */
export function buildLocalPortfolioReply(message, visitorMode, profile = profileFallback) {
  const query = sanitizeText(message, 1000).toLowerCase();
  const mode = normalizeVisitorMode(visitorMode) || VISITOR_MODES.BROWSING;
  const identity = profile?.identity || {};
  const experience = Array.isArray(profile?.experience) ? profile.experience : [];
  const topSkills = Array.isArray(profile?.topSkills) ? profile.topSkills : [];
  const languages = Array.isArray(profile?.languages) ? profile.languages : [];
  const certifications = Array.isArray(profile?.certifications) ? profile.certifications : [];
  const volunteerExperience = Array.isArray(profile?.volunteerExperience)
    ? profile.volunteerExperience
    : [];
  const hackathons = Array.isArray(profile?.hackathons) ? profile.hackathons : [];
  const linkedinProjects = Array.isArray(profile?.linkedinProjects) ? profile.linkedinProjects : [];
  const contact = profile?.contact || {};

  const modeAwareReply = (baseReply) => {
    if (mode !== VISITOR_MODES.HIRING) {
      return baseReply;
    }

    if (hasAnyTerm(query, ["salary", "ctc", "compensation", "pay"])) {
      return "Compensation expectations are best discussed directly with Ram. I can share role-fit evidence, impact highlights, and contact details.";
    }

    if (hasAnyTerm(query, ["hire", "hiring", "fit", "interview", "candidate", "recruiter", "position"])) {
      return `${buildHiringSnapshot(profile)} Evidence: ${baseReply}`;
    }

    return `${baseReply} ${buildHiringSnapshot(profile)}`;
  };

  if (!query) {
    if (mode === VISITOR_MODES.HIRING) {
      return "Please share the role or hiring criteria, and I will return a concise fit summary with evidence.";
    }

    return "Ask about projects, skills, certifications, experience, or contact details from the portfolio.";
  }

  if (hasAnyTerm(query, ["react", "next.js", "nextjs", "frontend", "ui", "javascript", "mern"])) {
    const matchedHighlights = collectExperienceHighlights(
      experience,
      ["react", "next.js", "frontend", "website", "migration", "dashboard"],
      3
    );

    if (matchedHighlights.length) {
      return modeAwareReply(`Yes. Ram has strong frontend delivery experience. ${matchedHighlights.join(" ")}`);
    }

    return modeAwareReply(
      "Yes. Ram has frontend experience, including React and modern UI engineering across product delivery roles."
    );
  }

  if (hasAnyTerm(query, ["skill", "skills", "tech stack", "technology", "technologies"])) {
    const skillSummary = topSkills
      .slice(0, 4)
      .map((skill) => `${sanitizeText(skill.name || "", 80)} (${skill.level || 0}%)`)
      .join(", ");
    const headline = sanitizeText(identity.headline || "", 220);

    return modeAwareReply(
      `Top skills include ${skillSummary || "Python, Machine Learning, and frontend engineering"}. Headline: ${headline}`
    );
  }

  if (hasAnyTerm(query, ["experience", "exp", "background", "work", "career"])) {
    const summary = experience
      .slice(0, 3)
      .map(
        (item) =>
          `${sanitizeText(item.role || "", 90)} at ${sanitizeText(item.company || "", 90)} (${sanitizeText(item.period || "", 60)})`
      )
      .join("; ");

    return modeAwareReply(`Recent experience: ${summary}`);
  }

  if (hasAnyTerm(query, ["cert", "certification", "pmle", "google cloud"])) {
    const certSummary = certifications.slice(0, 3).map((item) => sanitizeText(item, 140)).join("; ");
    return modeAwareReply(`Certifications include: ${certSummary}`);
  }

  if (hasAnyTerm(query, ["language", "languages", "speak"])) {
    const languageSummary = languages
      .map((item) => `${sanitizeText(item.name || "", 40)} (${sanitizeText(item.proficiency || "", 60)})`)
      .join(", ");
    return modeAwareReply(`Languages: ${languageSummary}`);
  }

  if (hasAnyTerm(query, ["volunteer", "volunteering", "community"])) {
    if (!volunteerExperience.length) {
      return modeAwareReply("Volunteer entries are not currently available in the synced dataset.");
    }

    const volunteerSummary = volunteerExperience
      .slice(0, 3)
      .map(
        (item) =>
          `${sanitizeText(item.role || "", 90)} at ${sanitizeText(item.organization || "", 90)} (${sanitizeText(item.period || "", 60)})`
      )
      .join("; ");

    return modeAwareReply(`Volunteer highlights: ${volunteerSummary}`);
  }

  if (hasAnyTerm(query, ["hackathon", "hackathons", "competition"])) {
    if (!hackathons.length) {
      return modeAwareReply("Hackathon entries are not currently available in the synced dataset.");
    }

    const hackathonSummary = hackathons
      .slice(0, 3)
      .map((item) => sanitizeText(item.name || "", 120))
      .join("; ");

    return modeAwareReply(`Hackathon highlights: ${hackathonSummary}`);
  }

  if (hasAnyTerm(query, ["contact", "email", "linkedin", "phone", "mobile", "reach"])) {
    return modeAwareReply(
      `You can reach Ram via email ${sanitizeText(contact.email || "", 120)}, LinkedIn ${sanitizeText(contact.linkedin || "", 180)}, GitHub ${sanitizeText(contact.github || "", 180)}, and Medium ${sanitizeText(contact.medium || "", 180)}.`
    );
  }

  if (hasAnyTerm(query, ["project", "projects", "github", "repo", "repository"])) {
    const githubUrl = sanitizeText(contact.github || "", 180);
    const linkedinProjectSummary = linkedinProjects
      .slice(0, 2)
      .map((project) => sanitizeText(project.title || "", 100))
      .join(" and ");

    if (linkedinProjectSummary) {
      return modeAwareReply(
        `Ram's project portfolio is available on GitHub: ${githubUrl}. LinkedIn-only project highlights include ${linkedinProjectSummary}.`
      );
    }

    return modeAwareReply(
      `Ram's project portfolio is available on GitHub: ${githubUrl}. I can summarize specific project areas like MLOps, reliability, or frontend architecture.`
    );
  }

  const summary = sanitizeText(identity.summary || "", 260);
  return modeAwareReply(
    `${summary} Ask me about experience, React/frontend work, skills, certifications, or projects.`
  );
}
